import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Palette, Plus, Save, Trash2, X } from "lucide-react";
import { GlassCard, PillButton, SectionTitle } from "./_shared";

export default function AdminFrontpage() {
  const [fp, setFp] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/frontpage");
    setFp(data);
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/frontpage", fp);
      toast.success("Frontpage enregistrée");
    } catch {
      toast.error("Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setFp((f) => ({ ...f, [k]: v }));

  const setFeature = (i, k, v) => {
    const next = [...(fp.features || [])];
    next[i] = { ...next[i], [k]: v };
    set("features", next);
  };
  const addFeature = () => set("features", [...(fp.features || []), { icon: "Sparkles", title: "Nouveau bloc", description: "Description courte" }]);
  const removeFeature = (i) => set("features", (fp.features || []).filter((_, j) => j !== i));

  if (!fp) return <div className="text-zinc-400 text-sm">Chargement…</div>;

  return (
    <div className="space-y-4">
      <SectionTitle icon={Palette}>Contenu de la landing</SectionTitle>

      <GlassCard className="p-4 space-y-3">
        <div>
          <div className="text-xs font-semibold text-zinc-400 mb-1.5">Titre principal</div>
          <input
            value={fp.hero_title || ""}
            onChange={(e) => set("hero_title", e.target.value)}
            className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-xl px-3 h-11 text-sm text-[var(--text)] outline-none focus:border-black"
            data-testid="fp-hero-title"
          />
        </div>
        <div>
          <div className="text-xs font-semibold text-zinc-400 mb-1.5">Sous-titre</div>
          <textarea
            rows={3}
            value={fp.hero_subtitle || ""}
            onChange={(e) => set("hero_subtitle", e.target.value)}
            className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-xl px-3 py-2 text-sm text-[var(--text)] outline-none focus:border-black resize-none"
            data-testid="fp-hero-subtitle"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-xs font-semibold text-zinc-400 mb-1.5">CTA principal</div>
            <input
              value={fp.hero_cta_primary || ""}
              onChange={(e) => set("hero_cta_primary", e.target.value)}
              className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-xl px-3 h-11 text-sm text-[var(--text)] outline-none focus:border-black"
              data-testid="fp-cta1"
            />
          </div>
          <div>
            <div className="text-xs font-semibold text-zinc-400 mb-1.5">CTA secondaire</div>
            <input
              value={fp.hero_cta_secondary || ""}
              onChange={(e) => set("hero_cta_secondary", e.target.value)}
              className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-xl px-3 h-11 text-sm text-[var(--text)] outline-none focus:border-black"
              data-testid="fp-cta2"
            />
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold text-zinc-400 mb-1.5">Footer tagline</div>
          <input
            value={fp.footer_tagline || ""}
            onChange={(e) => set("footer_tagline", e.target.value)}
            className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-xl px-3 h-11 text-sm text-[var(--text)] outline-none focus:border-black"
            data-testid="fp-footer"
          />
        </div>
      </GlassCard>

      <div className="flex items-center justify-between mt-6">
        <div className="text-[var(--text)] font-display font-bold text-lg">Features · {fp.features?.length || 0}</div>
        <PillButton variant="primary" onClick={addFeature} testId="fp-add-feature">
          <Plus className="w-3 h-3 inline mr-1" />Ajouter
        </PillButton>
      </div>

      <div className="space-y-3">
        {(fp.features || []).map((f, i) => (
          <GlassCard key={i} className="p-4" data-testid={`fp-feature-${i}`}>
            <div className="flex items-start gap-2">
              <div className="grid grid-cols-2 gap-2 flex-1">
                <input
                  placeholder="Icône (Lucide)"
                  value={f.icon || ""}
                  onChange={(e) => setFeature(i, "icon", e.target.value)}
                  className="bg-[var(--surface)] border border-[var(--border)] rounded-lg px-3 h-9 text-xs text-[var(--text)] outline-none focus:border-emerald-500"
                />
                <input
                  placeholder="Titre"
                  value={f.title || ""}
                  onChange={(e) => setFeature(i, "title", e.target.value)}
                  className="bg-[var(--surface)] border border-[var(--border)] rounded-lg px-3 h-9 text-xs text-[var(--text)] outline-none focus:border-emerald-500"
                />
                <textarea
                  rows={2}
                  placeholder="Description"
                  value={f.description || ""}
                  onChange={(e) => setFeature(i, "description", e.target.value)}
                  className="col-span-2 bg-[var(--surface)] border border-[var(--border)] rounded-xl px-3 py-2 text-xs text-[var(--text)] outline-none focus:border-black resize-none"
                />
              </div>
              <button
                onClick={() => removeFeature(i)}
                className="text-rose-300 hover:text-rose-300 w-8 h-8 rounded-lg bg-rose-500/100/10 border border-rose-400/20 grid place-items-center shrink-0"
                data-testid={`fp-remove-${i}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </GlassCard>
        ))}
      </div>

      <div className="sticky bottom-20 z-10 pt-2">
        <PillButton
          variant="primary"
          onClick={save}
          disabled={saving}
          testId="fp-save"
          className="w-full h-11 text-sm"
        >
          <Save className="w-3.5 h-3.5 inline mr-1" />{saving ? "Enregistrement…" : "Enregistrer la frontpage"}
        </PillButton>
      </div>
    </div>
  );
}
